import styled from 'styled-components';

/**
 * Inline alert banner shown during the activation wizard when a SIM
 * from an eSIM batch is detected. Non-blocking — the customer can
 * continue with activation and open the setup instructions directly.
 */
export const EsimUpdateBanner = ({ batchLabel }: { batchLabel: string }) => {
  return (
    <Banner>
      <BannerIcon>⚠️</BannerIcon>
      <BannerText>
        <strong>eSIM Update Required ({batchLabel})</strong>
        <p>
          Your SIM requires a one-time camera setting change before it will come online.
          Complete your activation, then follow the setup instructions below.
        </p>
        <HelpLink
          href="https://www.farmstream.co.uk/pages/esim-manual-update"
          target="_blank"
          rel="noopener noreferrer">
          View full instructions with images →
        </HelpLink>
      </BannerText>
    </Banner>
  );
};

/**
 * Detailed notice shown on the ThankYou / order-confirmation page
 * for affected SIMs after payment is complete.
 */
export const EsimUpdateThankYouNotice = ({ batchLabel }: { batchLabel: string }) => {
  return (
    <NoticeCard>
      <NoticeHeader>
        <span>⚠️</span>
        <h3>Important: eSIM Manual Update Required</h3>
      </NoticeHeader>

      <NoticeBody>
        <p>
          Your SIM is from <strong>{batchLabel}</strong> and requires a quick one-time
          setting change on your camera before it will come online.
        </p>
        <p>
          Follow these steps, or open the full instructions with images below:
        </p>

        <StepsList>
          <li>
            <strong>Connect to Camera WiFi</strong> — Network: <code>Farmstream</code>, Password: <code>1234567890</code>
          </li>
          <li>
            <strong>Open browser</strong> → go to <code>192.168.100.1</code> → login with password: <code>admin</code>
          </li>
          <li>
            <strong>Advanced Settings → Others</strong> → scroll to <em>SIM Card Selection</em>
          </li>
          <li>
            <strong>Change dropdown</strong> from <code>SIM</code> to <code>eSIM</code> → click <em>Apply</em>
          </li>
          <li>
            <strong>Restart</strong> the device when prompted
          </li>
        </StepsList>

        <HelpLink
          href="https://www.farmstream.co.uk/pages/esim-manual-update"
          target="_blank"
          rel="noopener noreferrer">
          View full instructions with images →
        </HelpLink>

        <Reassurance>
          This is a <strong>one-time change only</strong>. Once updated, your camera will operate
          normally. We apologise for the inconvenience.
        </Reassurance>
      </NoticeBody>
    </NoticeCard>
  );
};

/* ── Inline Banner styles (for activation wizard) ── */

const Banner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  max-width: 38rem;
  background-color: #fff3cd;
  border: 1px solid #ffecb5;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
`;

const BannerIcon = styled.span`
  font-size: 1.4rem;
  line-height: 1;
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

const BannerText = styled.div`
  flex: 1;

  strong {
    display: block;
    color: #664d03;
    font-size: ${({ theme }) => theme.fontSize.small};
    margin-bottom: 0.25rem;
  }

  p {
    margin: 0;
    color: #664d03;
    font-size: ${({ theme }) => theme.fontSize.extraSmall};
    line-height: 1.4;
  }
`;

/* ── ThankYou notice styles ── */

const NoticeCard = styled.div`
  width: 100%;
  max-width: 600px;
  border: 2px solid #ffecb5;
  border-radius: 0.5rem;
  overflow: hidden;
`;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #fff3cd;
  padding: 0.75rem 1rem;

  span {
    font-size: 1.3rem;
  }

  h3 {
    margin: 0;
    color: #664d03;
    font-size: ${({ theme }) => theme.fontSize.small};
  }
`;

const NoticeBody = styled.div`
  padding: 1rem 1.25rem;
  background: white;

  > p {
    margin: 0 0 0.75rem;
    font-size: ${({ theme }) => theme.fontSize.small};
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.gray900};
  }
`;

const StepsList = styled.ol`
  margin: 0.75rem 0;
  padding-left: 1.25rem;

  li {
    font-size: ${({ theme }) => theme.fontSize.small};
    line-height: 1.6;
    margin-bottom: 0.35rem;
    color: ${({ theme }) => theme.colors.gray900};
  }

  code {
    background: ${({ theme }) => theme.colors.gray};
    padding: 0.1rem 0.35rem;
    border-radius: 0.2rem;
    font-size: 0.85em;
  }
`;

const HelpLink = styled.a`
  display: inline-block;
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.small};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Reassurance = styled.p`
  && {
    margin-top: 1rem;
    font-size: ${({ theme }) => theme.fontSize.extraSmall};
    color: ${({ theme }) => theme.colors.gray500};
    text-align: center;
  }
`;
